import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Copy, Check, Sparkles, MessageCircle, Mic, Phone, Headphones, AudioLines, Code, Sun, Moon, Monitor, Image as ImageIcon, X, Upload, Palette } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import SubAccountLayout from '../../components/SubAccountLayout';
import BlackHoleOrb from '../../components/widgets/BlackHoleOrb';
import { DemoFolderFilter } from '../../components/leads/DemoFolderFilter';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';

// ─── Utility: contrast text color ──────────────────────────────
// Returns `#0f172a` (dark) or `#ffffff` (light) based on WCAG
// relative luminance. Threshold 0.55 matches the backend
// `_contrast_text_for(...)` helper so the live preview and the
// deployed widget agree on which colors are "light enough" to need
// dark text. Without this match the operator would see the preview
// switch to dark text but the live site stay white (or vice-versa).
const _hexLuminance = (hex) => {
  const h = String(hex || '').trim().replace(/^#/, '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (full.length !== 6) return 0.5;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const ch = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
};
const getContrastTextColor = (hex) => (_hexLuminance(hex) > 0.55 ? '#0f172a' : '#ffffff');

// Auto-resize a user-uploaded image to a 96×96 PNG via canvas before
// storing it as a base64 data-URL on the widget config. Keeps payloads
// tiny (~5-15 KB typical) so the backend's 600 KB cap is never hit
// from the UI in practice. Returns a Promise resolving to the data-URL.
const resizeImageToDataUrl = (file, target = 96, quality = 0.85) => new Promise((resolve, reject) => {
  if (!file || !file.type?.startsWith('image/')) {
    reject(new Error('Please choose an image file (PNG, JPG, or GIF).'));
    return;
  }
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = target; c.height = target;
      const ctx = c.getContext('2d');
      // Center-crop to square, then scale down to target.
      const min = Math.min(img.width, img.height);
      const sx = (img.width - min) / 2;
      const sy = (img.height - min) / 2;
      ctx.drawImage(img, sx, sy, min, min, 0, 0, target, target);
      try {
        resolve(c.toDataURL('image/png', quality));
      } catch (err) { reject(err); }
    };
    img.onerror = () => reject(new Error('Could not decode the selected image.'));
    img.src = e.target.result;
  };
  reader.onerror = () => reject(new Error('Could not read the selected file.'));
  reader.readAsDataURL(file);
});

/**
 * Widgets — Phase 1: Orb Creator + Chat Widget Creator.
 * Roomy split-screen pattern (left: editor, right: live preview).
 *
 * Iter133 changes:
 *  - Replaced VoiceOrb particle sphere with BlackHoleOrb (matches voxinity.ai hero)
 *  - Removed Position + Size from Orb tab (host page / LeadConnector embed handles both)
 *  - Fixed agent dropdown — endpoint returns bare array, not { agents: [] }
 *  - Spread layout to full width with generous breathing room
 */
export default function WidgetsPage() {
  const { subAccountId } = useParams();
  const { api } = useAuth();
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('orb'); // 'orb' | 'widget'
  const [agents, setAgents] = useState([]);
  const [orbConfigs, setOrbConfigs] = useState([]);
  const [widgetConfigs, setWidgetConfigs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);  // Get-Code modal
  const [chooserOpen, setChooserOpen] = useState(false);  // "Create new widget" type picker
  const [previewTheme, setPreviewTheme] = useState('auto');  // 'auto' | 'light' | 'dark' — preview-pane only
  const [previewOpen, setPreviewOpen] = useState(true);  // Chat widget preview: open vs closed launcher
  // Resolved theme actually used by the iframe + pane background. `auto` mirrors
  // the platform's current Light/Dark mode so demos can flip both at once.
  const effectiveTheme = previewTheme === 'auto' ? (isDarkMode ? 'dark' : 'light') : previewTheme;

  useEffect(() => {
    if (!subAccountId) return;
    (async () => {
      try {
        const [a, o, w] = await Promise.all([
          api.get(`/api/sub-accounts/${subAccountId}/agents`),
          api.get(`/api/sub-accounts/${subAccountId}/orb-configs`),
          api.get(`/api/sub-accounts/${subAccountId}/chat-widget-configs`),
        ]);
        // /agents returns a bare array (not wrapped in { agents: [] })
        setAgents(Array.isArray(a.data) ? a.data : (a.data?.agents || []));
        // Tag each config with its widget_type so we can render a mixed
        // list with type badges. Backend doesn't set this field (it's
        // implicit in the endpoint); we add it client-side.
        setOrbConfigs((o.data?.configs || []).map(c => ({ ...c, widget_type: 'orb' })));
        setWidgetConfigs((w.data?.configs || []).map(c => ({ ...c, widget_type: 'widget' })));
      } catch (e) { /* surfaces in toast on user action */ }
    })();
  }, [subAccountId]);

  const [brandColors, setBrandColors] = useState([]);
  useEffect(() => {
    api.get(`/api/sub-accounts/${subAccountId}/brand-profile`)
      .then(r => setBrandColors((((r.data || {}).brand || {}).colors || []).filter(Boolean).slice(0, 4)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subAccountId]);

  const lightenHex = (hex, amt = 60) => {
    let h = (hex || '').replace('#', '');
    if (h.length === 3) h = h.split('').map(ch => ch + ch).join('');
    if (h.length !== 6) return hex;
    const n = parseInt(h, 16);
    const r = Math.min(255, (n >> 16) + amt), g = Math.min(255, ((n >> 8) & 255) + amt), b = Math.min(255, (n & 255) + amt);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  };
  const applyBrandColors = () => {
    if (!brandColors.length) return;
    setEditing(ed => ({ ...ed, primary_color: brandColors[0], accent_color: brandColors[1] || lightenHex(brandColors[0]) }));
    toast.success('Brand colors applied');
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
  const list = activeTab === 'orb' ? orbConfigs : widgetConfigs;
  const endpoint = activeTab === 'orb' ? 'orb-configs' : 'chat-widget-configs';
  const embedPath = activeTab === 'orb' ? 'orb' : 'widget';

  // Merged list for the new unified grid view. Sort by updated_at desc so
  // recently-edited widgets float to the top regardless of type. Falls
  // back to created_at / name for configs missing an updated timestamp.
  const [demoFolder, setDemoFolder] = useState('production');
  const unifiedList = useMemo(() => {
    const ts = (c) => c?.updated_at || c?.created_at || c?.name || '';
    return [...orbConfigs, ...widgetConfigs].filter(c => demoFolder === 'all' || (demoFolder === 'demo' ? c.demo_only : !c.demo_only)).sort((a, b) => String(ts(b)).localeCompare(String(ts(a))));
  }, [orbConfigs, widgetConfigs, demoFolder]);

  const newDraft = (type) => type === 'orb' ? ({
    name: 'New Orb',
    agent_id: agents[0]?.agent_id || '',
    primary_color: '#1a5eff',
    accent_color: '#66ffe0',
    display_name: '',
    show_display_name: false,
    hint_text: 'Click to talk',
    show_hint: true,
    size: 'medium',  // small | medium
    icon: 'mic',     // mic | phone | message | headphones | waveform
    show_transcript: false,
    transcript_text_color: 'black',  // black | white
  }) : ({
    name: 'New Chat Widget',
    agent_id: agents[0]?.agent_id || '',
    primary_color: '#14b8a6',
    accent_color: '#a78bfa',
    position: 'bottom-right',
    display_name: '',
    welcome_message: 'Hi! How can I help you today?',
    suggested_prompts: [],  // array of strings, max 6
    launcher_style: 'bubble',
    launcher_label: 'Chat with us',
    launcher_icon: 'message',
    popup_message: '',
    show_popup: true,
    // Phase-3 pop-up style & avatar — defaults match the old colored
    // bubble so an out-of-the-box new widget renders identically. The
    // operator opts into the avatar style explicitly.
    popup_style: 'text',
    popup_avatar_url: '',
    popup_bubble_theme: 'light',
    auto_open: false,
    widget_theme: 'light',
    allowed_origins: [],
  });

  const startCreate = (type) => {
    // `type` comes from the chooser modal ('orb' | 'widget'). Fall back to
    // the currently-selected tab if invoked without an argument (legacy).
    if (type === 'orb' || type === 'widget') setActiveTab(type);
    setEditing(newDraft(type || activeTab));
  };
  const startEdit = (cfg) => {
    // Route the editor to the correct builder based on the saved widget's
    // type — the unified list mixes orbs and chat widgets.
    if (cfg.widget_type === 'orb' || cfg.widget_type === 'widget') {
      setActiveTab(cfg.widget_type);
    }
    setEditing({ ...cfg });
  };
  const [projectParams, setProjectParams] = useSearchParams();
  const openedProjectConfig = useRef('');
  const projectConfigId = projectParams.get('config');
  useEffect(() => {
    const key = `${subAccountId}:${projectConfigId}`;
    if (!projectConfigId || openedProjectConfig.current === key) return;
    const config = [...orbConfigs, ...widgetConfigs].find(c => c.config_id === projectConfigId);
    if (config) { openedProjectConfig.current = key; startEdit(config); }
    // Open the saved, tenant-scoped config once; polling or saving must not reset edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectConfigId, subAccountId, orbConfigs, widgetConfigs]);
  const cancelEdit = () => { setEditing(null); if (projectConfigId) setProjectParams(p => { const next = new URLSearchParams(p); next.delete('config'); return next; }); };

  const saveConfig = async () => {
    if (!editing.agent_id) { toast.error('Please select an agent'); return; }
    if (!editing.name?.trim()) { toast.error('Please enter a name'); return; }
    // Phase-3 guard: avatar+text pop-up requires an uploaded avatar.
    // Match the backend rule so the operator gets immediate feedback
    // instead of a delayed 400 after hitting Save.
    if (
      activeTab === 'widget' &&
      editing.popup_style === 'avatar_text' &&
      !(editing.popup_avatar_url || '').trim()
    ) {
      toast.error(
        "Upload an avatar image, or switch the pop-up style back to 'Text Only'."
      );
      return;
    }
    setSaving(true);
    try {
      const isNew = !editing.config_id;
      const url = isNew
        ? `/api/sub-accounts/${subAccountId}/${endpoint}`
        : `/api/sub-accounts/${subAccountId}/${endpoint}/${editing.config_id}`;
      const method = isNew ? 'post' : 'put';
      // Backend Orb model still accepts position/size — we just send sensible defaults
      // since they're no longer user-editable for orbs.
      const payload = activeTab === 'orb'
        ? {
            name: editing.name,
            agent_id: editing.agent_id,
            primary_color: editing.primary_color,
            accent_color: editing.accent_color,
            position: 'bottom-right',
            size: editing.size || 'medium',
            icon: editing.icon || 'mic',
            display_name: editing.display_name,
            show_display_name: !!editing.show_display_name,
            hint_text: editing.hint_text || '',
            show_hint: editing.show_hint !== false,
            show_transcript: !!editing.show_transcript,
            transcript_text_color: editing.transcript_text_color || 'black',
          }
        : {
            name: editing.name,
            agent_id: editing.agent_id,
            primary_color: editing.primary_color,
            accent_color: editing.accent_color,
            position: 'bottom-right',
            display_name: editing.display_name,
            welcome_message: editing.welcome_message,
            suggested_prompts: (editing.suggested_prompts || []).filter(p => p && p.trim()).slice(0, 6),
            launcher_style: editing.launcher_style || 'bubble',
            launcher_label: (editing.launcher_label || '').trim() || 'Chat with us',
            launcher_icon: editing.launcher_icon || 'message',
            popup_message: editing.popup_message || '',
            show_popup: editing.show_popup !== false,
            // Phase-3 pop-up style fields — MUST be included or the
            // backend stores its Pydantic defaults (`popup_style="text"`,
            // `popup_avatar_url=""`, `popup_bubble_theme="light"`) and the
            // operator's Avatar+Text choice silently reverts to the
            // colored text-only bubble. Same pattern users reported as
            // the "auto defaults to text only after save" bug.
            popup_style: editing.popup_style || 'text',
            popup_avatar_url: editing.popup_avatar_url || '',
            popup_bubble_theme: editing.popup_bubble_theme || 'light',
            auto_open: !!editing.auto_open,
            widget_theme: editing.widget_theme || 'light',
            allowed_origins: (editing.allowed_origins || []).map(o => (o || '').trim()).filter(Boolean),
          };
      const res = await api[method](url, payload);
      const saved = { ...res.data, widget_type: activeTab };
      if (activeTab === 'orb') {
        setOrbConfigs(prev => isNew ? [saved, ...prev] : prev.map(c => c.config_id === saved.config_id ? saved : c));
      } else {
        setWidgetConfigs(prev => isNew ? [saved, ...prev] : prev.map(c => c.config_id === saved.config_id ? saved : c));
      }
      setEditing(saved);
      toast.success(`${activeTab === 'orb' ? 'Orb' : 'Widget'} saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async (cfg) => {
    if (!window.confirm(`Delete "${cfg.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/${endpoint}/${cfg.config_id}`);
      if (activeTab === 'orb') setOrbConfigs(prev => prev.filter(c => c.config_id !== cfg.config_id));
      else setWidgetConfigs(prev => prev.filter(c => c.config_id !== cfg.config_id));
      if (editing?.config_id === cfg.config_id) setEditing(null);
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const embedSnippet = useMemo(() => {
    if (!editing?.config_id) return '';
    // Cache-bust on every save so re-pasting the snippet always pulls the latest themed iframe.
    const cacheBust = editing.updated_at ? encodeURIComponent(editing.updated_at) : Date.now();
    const src = `${apiBase}/api/embed/${embedPath}/${editing.config_id}?v=${cacheBust}`;
    const assistantId = editing.agent_id || '';
    if (activeTab === 'orb') {
      // Fixed pixel size matches the editor preview exactly. `text-align:center`
      // on the wrapper centers the inline-block iframe horizontally regardless
      // of the host container's flex/grid defaults, AND inline-block iframes
      // aren't stretched by flex parents (which was distorting the orb into an
      // oval in LeadConnector's code blocks). `aspect-ratio:1/1 !important` is a belt-
      // and-suspenders guard against host-applied zoom/scale transforms.
      const px = (editing.size || 'medium') === 'small' ? 220 : 320;
      // Transcript mode needs ~120px of extra height under the orb for the
      // rolling chat feed — keep width square so the orb itself never shrinks.
      const ph = px + (editing.show_transcript ? 120 : 0);
      return `<!-- Voxinity Orb · agent: ${assistantId} -->
<div style="text-align:center !important;width:100% !important;line-height:0 !important;">
  <iframe
    src="${src}"
    allow="microphone; autoplay; camera 'none'"
    width="${px}"
    height="${ph}"
    scrolling="no"
    frameborder="0"
    style="width:${px}px !important;height:${ph}px !important;min-width:${px}px !important;min-height:${ph}px !important;max-width:${px}px !important;max-height:${ph}px !important;aspect-ratio:${px}/${ph} !important;border:0 !important;background:transparent !important;display:inline-block !important;vertical-align:top !important;margin:0 auto !important;padding:0 !important;"
    id="voxinityOrb_${editing.config_id}"
    data-voxinity-assistant-id="${assistantId}"
  ></iframe>
</div>
<script>
  (function(){
    var f = document.getElementById('voxinityOrb_${editing.config_id}');
    if (!f) return;
    f.addEventListener('load', function(){
      if (navigator.permissions) {
        navigator.permissions.query({ name: 'microphone' }).catch(function(){});
      }
    });
  })();
</script>`;
    }
    // Chat Widget — uses the script-tag loader that injects a floating
    // bottom-right launcher onto the host page and opens the chat panel
    // in a CSS-isolated iframe. One-liner for customers — cache-busted
    // on every save so edits propagate within the loader's 60s cache.
    return `<!-- Voxinity Chat Widget · agent: ${assistantId} -->
<script src="${apiBase}/api/embed/widget/${editing.config_id}/loader.js?v=${cacheBust}" async></script>`;
  }, [editing?.config_id, editing?.agent_id, editing?.size, editing?.show_transcript, editing?.updated_at, embedPath, apiBase, activeTab]);

  const copySnippet = async () => {
    try { await navigator.clipboard.writeText(embedSnippet); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error('Copy failed'); }
  };

  // ─── Theme tokens ─── follows the platform Light/Dark setting via
  // `isDarkMode` from ThemeContext (same convention used in AIAgentsPage).
  // Centralised here so the entire page swaps in lockstep instead of
  // sprinkling `dark:` modifiers everywhere.
  const t = {
    bgMain: isDarkMode ? 'bg-[#08090d]' : 'bg-gray-50',
    text: isDarkMode ? 'text-white' : 'text-gray-900',
    textMuted: isDarkMode ? 'text-gray-400' : 'text-gray-600',
    textFaint: isDarkMode ? 'text-gray-500' : 'text-gray-500',
    label: isDarkMode ? 'text-gray-300' : 'text-gray-700',
    cardBg: isDarkMode ? 'bg-white/[0.02]' : 'bg-white',
    cardBorder: isDarkMode ? 'border-white/10' : 'border-gray-200',
    cardBorderHover: isDarkMode ? 'hover:border-teal-400/30' : 'hover:border-teal-400/60',
    inputBg: isDarkMode ? 'bg-white/5' : 'bg-white',
    inputBorder: isDarkMode ? 'border-white/10' : 'border-gray-300',
    pillBg: isDarkMode ? 'bg-white/5' : 'bg-gray-100',
    pillHover: isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-200',
    dashedBorder: isDarkMode ? 'border-white/10' : 'border-gray-300',
    btnGhostText: isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900',
    iconMuted: isDarkMode ? 'text-gray-500' : 'text-gray-400',
  };

  return (
    <SubAccountLayout>
    <div className={`${editing ? 'xl:h-screen xl:overflow-hidden xl:flex xl:flex-col' : 'min-h-screen'} ${t.bgMain} ${t.text}`}>
      <div className={`px-10 ${editing ? 'pt-10 pb-0 xl:flex-1 xl:min-h-0 xl:flex xl:flex-col' : 'py-10'}`}>
        {/* Header + primary action */}
        <div className="mb-10 flex items-center justify-between xl:shrink-0">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Widgets</h1>
            <p className={`text-sm ${t.textMuted} mt-1.5`}>Embeddable Orb &amp; Chat Widget for any website</p>
          </div>
          {!editing && (
            <button
              onClick={() => setChooserOpen(true)}
              data-testid="open-create-widget-modal"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-teal-400/15 hover:bg-teal-400/25 text-teal-500 text-sm font-medium transition-colors border border-teal-400/20"
            >
              <Plus size={15} strokeWidth={2.25} /> Create New Widget
            </button>
          )}
        </div>

        {!editing && <DemoFolderFilter value={demoFolder} onChange={setDemoFolder} scope="widgets"/>}
        {/* List view (no active editing) */}
        {!editing ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {unifiedList.map(cfg => {
              const isOrb = cfg.widget_type === 'orb';
              // Badge tokens — teal for orb, violet for chat widget. Kept
              // subtle so the card still reads as "a saved thing" not a
              // status chip.
              const badgeBg = isOrb
                ? (isDarkMode ? 'bg-teal-400/15 text-teal-400 border-teal-400/25' : 'bg-teal-500/10 text-teal-700 border-teal-500/30')
                : (isDarkMode ? 'bg-violet-400/15 text-violet-300 border-violet-400/25' : 'bg-violet-500/10 text-violet-700 border-violet-500/30');
              return (
                <motion.div
                  key={cfg.config_id}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -2 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                  className={`relative rounded-2xl border ${t.cardBorder} ${t.cardBg} p-6 ${t.cardBorderHover} transition-shadow cursor-pointer group min-h-[200px] flex flex-col justify-between ${isDarkMode ? 'hover:shadow-2xl hover:shadow-black/40' : 'shadow-sm hover:shadow-lg'}`}
                  onClick={() => startEdit(cfg)}
                  data-testid={`config-card-${cfg.config_id}`}
                >
                  {/* Type badge — top-right corner */}
                  <span
                    className={`absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${badgeBg}`}
                    data-testid={`type-badge-${cfg.config_id}`}
                  >
                    {isOrb ? <Sparkles size={10} strokeWidth={2.5} /> : <MessageCircle size={10} strokeWidth={2.5} />}
                    {isOrb ? 'Orb' : 'Chat Widget'}
                  </span>
                  <div className="flex items-start justify-between pr-20">
                    {/* Type icon — mirrors the chooser modal so the cards
                        feel like instances of the same primitive. Orb
                        cards get a blue→navy radial gradient circle with
                        a mic; chat widget cards get a violet→indigo
                        rounded-square with a chat bubble. The widget's
                        own primary/accent colors are surfaced elsewhere
                        (live preview + embed) — this icon only signals
                        the widget *type*. */}
                    {isOrb ? (
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg shrink-0"
                        style={{ background: 'radial-gradient(circle at 35% 30%, #66ffe0 0%, #1a5eff 55%, #0a0a23 100%)' }}
                      >
                        <Mic size={20} strokeWidth={2} className="text-white/90" />
                      </div>
                    ) : (
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg shrink-0"
                        style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 60%, #4c1d95 100%)' }}
                      >
                        <MessageCircle size={20} strokeWidth={2} className="text-white/90" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-end justify-between gap-3">
                    <div className="min-w-0">
                      <p className={`text-base font-medium ${t.text} truncate`}>{cfg.name}</p>
                      <p className={`text-xs ${t.textFaint} mt-1 truncate`}>{cfg.agent_name || cfg.agent_id}</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); deleteConfig(cfg); }} className={`shrink-0 opacity-0 group-hover:opacity-100 ${t.iconMuted} hover:text-red-400 transition`} data-testid={`delete-${cfg.config_id}`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
            {unifiedList.length === 0 && (
              <div className={`col-span-full text-center py-16 ${t.textFaint} text-sm`}>
                No widgets yet — click <span className="text-teal-500 font-medium">Create New Widget</span> to get started.
              </div>
            )}
          </div>
        ) : (
          /* Editor view — viewport-locked at xl+.
             Page itself doesn't scroll; only the LEFT column does. */
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,520px)_minmax(0,1fr)] gap-10 xl:flex-1 xl:min-h-0 xl:pb-10">
            {/* LEFT — Editor (own scroll at xl+) */}
            <div className="space-y-7 xl:overflow-y-auto xl:min-h-0 xl:pr-3 xl:-mr-3" data-testid="widget-editor-panel">
              <div className="flex items-center justify-between">
                <button onClick={cancelEdit} className={`text-sm ${t.btnGhostText}`} data-testid="back-to-list">← Back to list</button>
                <div className="flex items-center gap-2">
                  <button onClick={cancelEdit} className={`px-4 py-2 text-sm rounded-lg ${t.pillBg} ${t.pillHover} ${t.label}`}>Cancel</button>
                  <button onClick={saveConfig} disabled={saving} data-testid="save-widget-config"
                    className="px-4 py-2 text-sm rounded-lg bg-teal-400/15 text-teal-500 hover:bg-teal-400/25 disabled:opacity-50 flex items-center gap-2">
                    <Save size={14} /> {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>

              <Field label="Internal name (admin only)" t={t}>
                <input type="text" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })}
                  className={`w-full px-4 py-3 rounded-xl ${t.inputBg} border ${t.inputBorder} text-sm focus:outline-none focus:border-teal-400/50 ${t.text}`}
                  data-testid="field-name" />
              </Field>

              {/* Display name + hint text intentionally removed from the orb
                  tab. The orb is a circular mic icon — adding a name or hint
                  underneath it feels spammy next to the more important orb
                  itself. Keep these fields for the chat widget tab only. */}
              {activeTab === 'widget' && (
                <Field label="Display name (shown to visitors)" t={t}>
                  <input type="text" value={editing.display_name || ''} onChange={e => setEditing({ ...editing, display_name: e.target.value })}
                    placeholder={editing.name}
                    className={`w-full px-4 py-3 rounded-xl ${t.inputBg} border ${t.inputBorder} text-sm focus:outline-none focus:border-teal-400/50 ${t.text}`} />
                </Field>
              )}

              <Field label="AI Agent" hint="The AI always speaks first using this agent's first-message setting." t={t}>
                <select value={editing.agent_id} onChange={e => setEditing({ ...editing, agent_id: e.target.value })}
                  className={`w-full px-4 py-3 rounded-xl ${t.inputBg} border ${t.inputBorder} text-sm focus:outline-none focus:border-teal-400/50 ${t.text}`}
                  data-testid="field-agent">
                  <option value="">Select an agent…</option>
                  {agents.map(a => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}
                </select>
                {agents.length === 0 && (
                  <p className="text-[11px] text-amber-500 mt-2">No agents yet — create one in AI Agents first.</p>
                )}
              </Field>

              {brandColors.length > 0 && (
                <button onClick={applyBrandColors} data-testid="use-brand-colors-btn"
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 rounded-xl border transition ${t.inputBg} ${t.inputBorder} hover:border-teal-400/50`}>
                  <Palette size={14} className="text-teal-400 shrink-0" />
                  <span className={`text-xs font-semibold ${t.text}`}>Use brand colors</span>
                  <span className={`text-[10px] ${t.btnGhostText}`}>from your Brand Profile</span>
                  <span className="ml-auto flex gap-1.5">
                    {brandColors.map((c, i) => <span key={i} className="w-4 h-4 rounded-full border border-white/20" style={{ background: c }} />)}
                  </span>
                </button>
              )}

              <div className="grid grid-cols-2 gap-5">
                <Field label={activeTab === 'widget' ? 'Widget color' : 'Orb color'} t={t}>
                  <ColorPicker value={editing.primary_color} onChange={v => setEditing({ ...editing, primary_color: v })} testId="field-primary-color" t={t} />
                </Field>
                <Field label="Accent color" hint="Used for the center icon only." t={t}>
                  <ColorPicker value={editing.accent_color} onChange={v => setEditing({ ...editing, accent_color: v })} testId="field-accent-color" t={t} />
                </Field>
              </div>

              {activeTab === 'orb' && (
                <Field label="Orb size" hint="Choose the size that matches your host page best. Host CSS still wins." t={t}>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: 'small',  label: 'Small',  px: '160px' },
                      { v: 'medium', label: 'Medium', px: '240px' },
                    ].map(s => (
                      <button key={s.v} onClick={() => setEditing({ ...editing, size: s.v })}
                        className={`px-3 py-2.5 text-sm rounded-lg border transition ${(editing.size || 'medium') === s.v ? 'bg-teal-400/15 border-teal-400/50 text-teal-500' : `${t.inputBg} ${t.inputBorder} ${t.btnGhostText}`}`}
                        data-testid={`orb-size-${s.v}`}
                      >
                        <div>{s.label}</div>
                        <div className="text-[10px] opacity-60">{s.px}</div>
                      </button>
                    ))}
                  </div>
                </Field>
              )}

              {activeTab === 'orb' && (
                <Field label="Live transcript" hint="During a call, shows the latest visitor + AI line under the orb. Replaces the status pill while talking." t={t}>
                  <label className={`flex items-center gap-2 text-xs ${t.textMuted} cursor-pointer select-none`}>
                    <input
                      type="checkbox"
                      checked={!!editing.show_transcript}
                      onChange={(e) => setEditing({ ...editing, show_transcript: e.target.checked })}
                      className="w-3.5 h-3.5 accent-teal-500"
                      data-testid="orb-show-transcript-toggle"
                    />
                    Show live transcript under the orb
                  </label>
                  {!!editing.show_transcript && (
                    <div className="mt-3">
                      <div className={`text-[11px] font-semibold mb-1.5 ${t.label}`}>Transcript text color</div>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { v: 'black', label: 'Black text', sub: 'For light websites' },
                          { v: 'white', label: 'White text', sub: 'For dark websites' },
                        ].map(o => {
                          const sel = (editing.transcript_text_color || 'black') === o.v;
                          return (
                            <button key={o.v}
                              onClick={() => setEditing({ ...editing, transcript_text_color: o.v })}
                              className={`px-3 py-2.5 text-sm rounded-lg border transition ${sel ? 'bg-teal-400/15 border-teal-400/50 text-teal-500' : `${t.inputBg} ${t.inputBorder} ${t.btnGhostText}`}`}
                              data-testid={`transcript-text-${o.v}`}
                            >
                              <div>{o.label}</div>
                              <div className="text-[10px] opacity-60">{o.sub}</div>
                            </button>
                          );
                        })}
                      </div>
                      <p className={`text-[11px] mt-1.5 ${t.textFaint}`}>Speaker names always use your orb color.</p>
                    </div>
                  )}
                </Field>
              )}

              {activeTab === 'orb' && (
                <Field label="Center icon" hint="The icon that floats in the middle of the orb." t={t}>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { v: 'mic',        label: 'Mic',     Icon: Mic },
                      { v: 'phone',      label: 'Phone',   Icon: Phone },
                      { v: 'message',    label: 'Message', Icon: MessageCircle },
                      { v: 'headphones', label: 'Audio',   Icon: Headphones },
                      { v: 'waveform',   label: 'Wave',    Icon: AudioLines },
                    ].map(({ v, label, Icon }) => (
                      <button key={v} onClick={() => setEditing({ ...editing, icon: v })}
                        className={`flex flex-col items-center justify-center gap-1 px-2 py-3 rounded-lg border transition ${(editing.icon || 'mic') === v ? 'bg-teal-400/15 border-teal-400/50 text-teal-500' : `${t.inputBg} ${t.inputBorder} ${t.btnGhostText}`}`}
                        data-testid={`orb-icon-${v}`}
                      >
                        <Icon size={18} strokeWidth={1.75} />
                        <span className="text-[10px]">{label}</span>
                      </button>
                    ))}
                  </div>
                </Field>
              )}

              {/* Hint text intentionally removed — the mic icon and halo
                  are sufficient affordance; adding "Click to talk" below
                  the orb looks dated next to the modern visual. */}

              {/* Position defaults to bottom-right for chat widgets — no
                  picker needed. The embed CSS anchors it there. */}

              {/* ─── Chat Widget: launcher style ─── */}
              {activeTab === 'widget' && (
                <Field label="Launcher style" hint="What visitors see before opening the chat." t={t}>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: 'bubble',    label: 'Bubble',    desc: 'Round chat icon' },
                      { v: 'rectangle', label: 'Rectangle', desc: 'Pill with label' },
                    ].map(s => {
                      const sel = (editing.launcher_style || 'bubble') === s.v;
                      return (
                        <button
                          key={s.v}
                          onClick={() => setEditing({ ...editing, launcher_style: s.v })}
                          className={`px-3 py-3 text-sm rounded-lg border transition text-left ${sel ? 'bg-teal-400/15 border-teal-400/50 text-teal-500' : `${t.inputBg} ${t.inputBorder} ${t.btnGhostText}`}`}
                          data-testid={`launcher-style-${s.v}`}
                        >
                          <div className="font-medium">{s.label}</div>
                          <div className="text-[10px] opacity-70 mt-0.5">{s.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </Field>
              )}

              {/* Rectangle launcher label — only when rectangle is chosen */}
              {activeTab === 'widget' && (editing.launcher_style || 'bubble') === 'rectangle' && (
                <Field label="Launcher label" hint="Text shown inside the rectangle button." t={t}>
                  <input
                    type="text"
                    value={editing.launcher_label || ''}
                    onChange={(e) => setEditing({ ...editing, launcher_label: e.target.value })}
                    placeholder="Chat with us"
                    maxLength={40}
                    className={`w-full px-4 py-3 rounded-xl ${t.inputBg} border ${t.inputBorder} text-sm focus:outline-none focus:border-teal-400/50 ${t.text}`}
                    data-testid="launcher-label-input"
                  />
                </Field>
              )}

              {/* Launcher icon — the glyph inside the bubble or next to
                  the rectangle label. Same five-icon grid as the orb for
                  consistency. */}
              {activeTab === 'widget' && (
                <Field label="Launcher icon" t={t}>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { v: 'mic',        label: 'Mic',     Icon: Mic },
                      { v: 'phone',      label: 'Phone',   Icon: Phone },
                      { v: 'message',    label: 'Message', Icon: MessageCircle },
                      { v: 'headphones', label: 'Audio',   Icon: Headphones },
                      { v: 'waveform',   label: 'Wave',    Icon: AudioLines },
                    ].map(({ v, label, Icon }) => {
                      const sel = (editing.launcher_icon || 'message') === v;
                      return (
                        <button
                          key={v}
                          onClick={() => setEditing({ ...editing, launcher_icon: v })}
                          className={`flex flex-col items-center justify-center gap-1 px-2 py-3 rounded-lg border transition ${sel ? 'bg-teal-400/15 border-teal-400/50 text-teal-500' : `${t.inputBg} ${t.inputBorder} ${t.btnGhostText}`}`}
                          data-testid={`launcher-icon-${v}`}
                        >
                          <Icon size={18} strokeWidth={1.75} />
                          <span className="text-[10px]">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </Field>
              )}

              {/* Pop-up teaser message next to the closed launcher — bubble-only.
                  Rectangle already carries its own label, so a second teaser
                  next to it would be visual noise. Textarea (not input) so
                  operators can press Enter for a second line — handy for
                  personalizing ("Hi 👋\nWelcome back, ready to book?"). */}
              {activeTab === 'widget' && (editing.launcher_style || 'bubble') === 'bubble' && (
                <Field label="Pop-up message" hint="A short teaser that appears above the launcher. Press Enter for a new line to personalize. Leave blank to hide." t={t}>
                  <textarea
                    value={editing.popup_message || ''}
                    onChange={(e) => setEditing({ ...editing, popup_message: e.target.value })}
                    placeholder={"👋 Got a question?\nAsk away!"}
                    rows={2}
                    maxLength={140}
                    className={`w-full px-4 py-3 rounded-xl ${t.inputBg} border ${t.inputBorder} text-sm focus:outline-none focus:border-teal-400/50 ${t.text} resize-none`}
                    style={{ whiteSpace: 'pre-wrap' }}
                    data-testid="popup-message-input"
                  />
                  <label className={`mt-2 flex items-center gap-2 text-xs ${t.textMuted} cursor-pointer select-none`}>
                    <input
                      type="checkbox"
                      checked={editing.show_popup !== false}
                      onChange={(e) => setEditing({ ...editing, show_popup: e.target.checked })}
                      className="w-3.5 h-3.5 accent-teal-500"
                      data-testid="show-popup-toggle"
                    />
                    Show pop-up above the launcher
                  </label>
                </Field>
              )}

              {/* Pop-up style — Text only vs Avatar + Text. Bubble launcher only.
                  Switching to `Avatar + Text` reveals the avatar upload tile
                  and the bubble-theme picker below; switching back hides them.
                  The backend enforces the same "must upload" rule, but we
                  validate client-side first for instant feedback. */}
              {activeTab === 'widget' && (editing.launcher_style || 'bubble') === 'bubble' && (
                <Field label="Pop-up style" hint="Pick how the pop-up bubble looks above the launcher." t={t}>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: 'text',        label: 'Text only',   sub: 'Colored bubble, just text' },
                      { v: 'avatar_text', label: 'Avatar + Text', sub: 'White card with avatar' },
                    ].map(o => {
                      const sel = (editing.popup_style || 'text') === o.v;
                      return (
                        <button
                          key={o.v}
                          onClick={() => setEditing({ ...editing, popup_style: o.v })}
                          className={`px-3 py-3 text-left rounded-lg border transition ${sel ? 'bg-teal-400/15 border-teal-400/50 text-teal-500' : `${t.inputBg} ${t.inputBorder} ${t.btnGhostText}`}`}
                          data-testid={`popup-style-${o.v}`}
                        >
                          <div className="text-sm font-medium">{o.label}</div>
                          <div className="text-[10px] opacity-60">{o.sub}</div>
                        </button>
                      );
                    })}
                  </div>
                </Field>
              )}

              {/* Avatar upload — only when popup_style is avatar_text. Files
                  are resized client-side to 96×96 PNG before being stored
                  as a data-URL on the widget config (typical payload 5-15
                  KB after PNG compression). The backend cap is 600 KB so
                  even very large source images stay well under the limit. */}
              {activeTab === 'widget' && (editing.launcher_style || 'bubble') === 'bubble' && (editing.popup_style || 'text') === 'avatar_text' && (
                <Field label="Avatar image" hint="A small round image that appears next to the pop-up message and in the chat header. Square images work best — non-square is auto-cropped." t={t}>
                  <AvatarUploader
                    value={editing.popup_avatar_url || ''}
                    onChange={(dataUrl) => setEditing({ ...editing, popup_avatar_url: dataUrl })}
                    t={t}
                  />
                </Field>
              )}

              {/* Pop-up bubble theme — only relevant when the bubble is the
                  Avatar + Text style. Text-only inherits the widget color
                  + auto-contrast (no choice needed). Light vs Dark only,
                  per the user's pick. */}
              {activeTab === 'widget' && (editing.launcher_style || 'bubble') === 'bubble' && (editing.popup_style || 'text') === 'avatar_text' && (
                <Field label="Pop-up bubble theme" hint="The bubble background — the launcher icon always stays your Widget color." t={t}>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: 'light', label: 'Light' },
                      { v: 'dark',  label: 'Dark' },
                    ].map(m => {
                      const sel = (editing.popup_bubble_theme || 'light') === m.v;
                      return (
                        <button
                          key={m.v}
                          onClick={() => setEditing({ ...editing, popup_bubble_theme: m.v })}
                          className={`px-3 py-2.5 text-sm rounded-lg border transition ${sel ? 'bg-teal-400/15 border-teal-400/50 text-teal-500' : `${t.inputBg} ${t.inputBorder} ${t.btnGhostText}`}`}
                          data-testid={`popup-bubble-theme-${m.v}`}
                        >
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              )}

              {/* Widget position — left or right side of the visitor's screen.
                  Backend stores as `bottom-right` / `bottom-left`. */}
              {activeTab === 'widget' && (
                <Field label="Position on the page" hint="Which side of the screen the launcher sits on." t={t}>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: 'bottom-right', label: 'Right side' },
                      { v: 'bottom-left',  label: 'Left side' },
                    ].map(p => {
                      const sel = (editing.position || 'bottom-right') === p.v;
                      return (
                        <button
                          key={p.v}
                          onClick={() => setEditing({ ...editing, position: p.v })}
                          className={`px-3 py-2.5 text-sm rounded-lg border transition ${sel ? 'bg-teal-400/15 border-teal-400/50 text-teal-500' : `${t.inputBg} ${t.inputBorder} ${t.btnGhostText}`}`}
                          data-testid={`widget-position-${p.v}`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              )}

              {/* Auto-open behaviour */}
              {activeTab === 'widget' && (
                <Field label="Auto-open" hint="Opens the chat automatically when the page loads — good for aggressive lead capture, distracting for support." t={t}>
                  <label className="flex items-center justify-between px-4 py-3 rounded-xl border cursor-pointer select-none"
                    style={{ borderColor: (editing.auto_open ? '#14b8a6' : undefined) }}
                  >
                    <span className={`text-sm ${t.text}`}>Open chat on page load</span>
                    <input
                      type="checkbox"
                      checked={!!editing.auto_open}
                      onChange={(e) => setEditing({ ...editing, auto_open: e.target.checked })}
                      className="w-4 h-4 accent-teal-500"
                      data-testid="auto-open-toggle"
                    />
                  </label>
                </Field>
              )}

              {/* Widget visual theme — independent of the platform theme.
                  Light or Dark only (Auto dropped — operators prefer an
                  explicit, predictable choice). */}
              {activeTab === 'widget' && (
                <Field label="Widget theme" hint="How the widget looks on your visitor's screen." t={t}>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: 'light', label: 'Light' },
                      { v: 'dark',  label: 'Dark' },
                    ].map(m => {
                      const sel = (editing.widget_theme || 'light') === m.v;
                      return (
                        <button
                          key={m.v}
                          onClick={() => setEditing({ ...editing, widget_theme: m.v })}
                          className={`px-3 py-2.5 text-sm rounded-lg border transition ${sel ? 'bg-teal-400/15 border-teal-400/50 text-teal-500' : `${t.inputBg} ${t.inputBorder} ${t.btnGhostText}`}`}
                          data-testid={`widget-theme-${m.v}`}
                        >
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              )}

              {activeTab === 'widget' && (
                <Field label="Welcome message" t={t}>
                  <textarea value={editing.welcome_message || ''} onChange={e => setEditing({ ...editing, welcome_message: e.target.value })}
                    rows={3}
                    className={`w-full px-4 py-3 rounded-xl ${t.inputBg} border ${t.inputBorder} text-sm focus:outline-none focus:border-teal-400/50 resize-none ${t.text}`} />
                </Field>
              )}

              {activeTab === 'widget' && (
                <Field label={`Suggested prompts (${(editing.suggested_prompts || []).length}/6)`} hint="Optional. Shown as quick-tap buttons under the welcome message. Visitors click one to send it instantly." t={t}>
                  <div className="space-y-2">
                    {(editing.suggested_prompts || []).map((p, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input type="text" value={p} onChange={e => {
                            const next = [...(editing.suggested_prompts || [])];
                            next[idx] = e.target.value;
                            setEditing({ ...editing, suggested_prompts: next });
                          }}
                          placeholder="e.g. Buying property"
                          className={`flex-1 px-4 py-2.5 rounded-lg ${t.inputBg} border ${t.inputBorder} text-sm focus:outline-none focus:border-teal-400/50 ${t.text}`}
                          data-testid={`prompt-input-${idx}`}
                        />
                        <button type="button"
                          onClick={() => setEditing({ ...editing, suggested_prompts: (editing.suggested_prompts || []).filter((_, i) => i !== idx) })}
                          className={`p-2 rounded-lg ${t.iconMuted} hover:text-rose-400 hover:bg-rose-400/10`}
                          data-testid={`prompt-remove-${idx}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                    {(editing.suggested_prompts || []).length < 6 && (
                      <button type="button"
                        onClick={() => setEditing({ ...editing, suggested_prompts: [...(editing.suggested_prompts || []), ''] })}
                        className={`w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg border border-dashed ${t.dashedBorder} text-xs ${t.btnGhostText} hover:border-teal-400/40 hover:text-teal-500`}
                        data-testid="add-prompt-btn"
                      >
                        <Plus size={13} /> Add prompt
                      </button>
                    )}
                  </div>
                </Field>
              )}

              {/* Get Code button — replaces the always-visible embed snippet
                  with a cleaner single button that opens a modal on demand.
                  Only appears after the config is saved (has config_id) since
                  the snippet needs a permanent URL to be copy-ready. */}
              {editing.config_id && (
                <button
                  type="button"
                  onClick={() => setCodeOpen(true)}
                  data-testid="open-embed-code-modal"
                  className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl border border-emerald-400/30 bg-emerald-400/10 hover:bg-emerald-400/20 text-emerald-500 text-sm font-medium transition-colors"
                >
                  <Code size={15} />
                  Get embed code
                </button>
              )}
            </div>

            {/* RIGHT — Live Preview. Fixed natural height so the chat panel
                fits exactly without forcing the preview pane to scroll
                alongside the form on the left. */}
            <div data-testid="widget-preview-panel">
              <div
                className={`rounded-2xl border h-[720px] relative overflow-hidden transition-colors duration-300 ${
                  effectiveTheme === 'light'
                    ? 'border-black/10 bg-gradient-to-br from-white via-gray-50 to-gray-100'
                    : 'border-white/10 bg-gradient-to-br from-white/[0.02] via-transparent to-transparent'
                }`}
              >
                <div className="absolute top-5 left-5 text-[11px] uppercase tracking-[0.18em] text-gray-500 flex items-center gap-3">
                  <span>Live preview</span>
                  {/* Open/Closed view toggle — chat widget only. Lets
                      operators audition the launcher + popup without
                      saving, then flip back to the open chat view. */}
                  {activeTab === 'widget' && (
                    <button
                      onClick={() => setPreviewOpen(o => !o)}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider border ${
                        effectiveTheme === 'light'
                          ? 'border-black/15 bg-white/80 text-gray-700 hover:bg-white'
                          : 'border-white/15 bg-black/40 text-gray-200 hover:bg-black/60'
                      } transition-colors`}
                      data-testid="preview-open-toggle"
                    >
                      {previewOpen ? '⌄ Open' : '⌃ Closed'}
                    </button>
                  )}
                </div>

                {/* Independent theme toggle for the preview iframe.
                    Three modes:
                      • Auto    — follows the platform's current Light/Dark mode.
                      • Light   — forces a white preview backdrop.
                      • Dark    — forces a dark preview backdrop.
                    The iframe itself uses a transparent background — the
                    surrounding swatch + the `_theme` query-param flip the
                    text/pill colors inside the embed so contrast stays
                    readable on both. */}
                <div className={`absolute top-4 right-4 z-10 flex items-center gap-1 rounded-full border ${effectiveTheme === 'light' ? 'border-black/10 bg-white/70' : 'border-white/10 bg-black/40'} backdrop-blur-md p-1`}>
                  <button
                    type="button"
                    onClick={() => setPreviewTheme('auto')}
                    data-testid="preview-theme-auto"
                    aria-label="Match platform theme"
                    title="Auto (match platform)"
                    className={`p-1.5 rounded-full transition-colors ${
                      previewTheme === 'auto'
                        ? 'bg-teal-400 text-gray-900'
                        : (effectiveTheme === 'light' ? 'text-gray-500 hover:text-gray-900' : 'text-gray-400 hover:text-white')
                    }`}
                  >
                    <Monitor size={14} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewTheme('light')}
                    data-testid="preview-theme-light"
                    aria-label="Preview on light background"
                    title="Light"
                    className={`p-1.5 rounded-full transition-colors ${
                      previewTheme === 'light'
                        ? 'bg-amber-300 text-gray-900'
                        : (effectiveTheme === 'light' ? 'text-gray-500 hover:text-gray-900' : 'text-gray-400 hover:text-white')
                    }`}
                  >
                    <Sun size={14} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewTheme('dark')}
                    data-testid="preview-theme-dark"
                    aria-label="Preview on dark background"
                    title="Dark"
                    className={`p-1.5 rounded-full transition-colors ${
                      previewTheme === 'dark'
                        ? 'bg-gray-900 text-white'
                        : (effectiveTheme === 'light' ? 'text-gray-500 hover:text-gray-900' : 'text-gray-400 hover:text-white')
                    }`}
                  >
                    <Moon size={14} strokeWidth={2} />
                  </button>
                </div>

                {activeTab === 'orb' ? (
                  <div className="flex flex-col items-center justify-center min-h-[700px] py-16">
                    {/* Live Preview is the SAME iframe the user copies into LeadConnector — single
                        source of truth, zero drift. Cache-buster keys off updated_at +
                        any local edit so the preview re-fetches the moment a config
                        changes (size, color, icon). Unsaved edits flow through
                        `_pc / _ac / _size / _icon / ...` query overrides. For brand-new
                        unsaved configs (no config_id yet) falls back to BlackHoleOrb. */}
                    {editing.config_id ? (() => {
                      const previewParams = new URLSearchParams({
                        v: String(editing.updated_at || Date.now()),
                        _pc: editing.primary_color || '',
                        _ac: editing.accent_color || '',
                        _size: editing.size || 'medium',
                        _icon: editing.icon || 'mic',
                        _theme: effectiveTheme,
                        _st: editing.show_transcript ? '1' : '0',
                        _ttc: editing.transcript_text_color || 'black',
                        _demo: '1',
                      });
                      const previewSrc = `${apiBase}/api/embed/orb/${editing.config_id}?${previewParams.toString()}`;
                      const px = (editing.size || 'medium') === 'small' ? 220 : 320;
                      const ph = px + (editing.show_transcript ? 120 : 0);
                      return (
                        <iframe
                          key={previewSrc}
                          src={previewSrc}
                          title="Live preview"
                          scrolling="no"
                          frameBorder="0"
                          style={{ width: px, height: ph, border: 0, background: 'transparent', display: 'block' }}
                          data-testid="widget-preview-iframe"
                        />
                      );
                    })() : (
                      <BlackHoleOrb
                        size={editing.size === 'small' ? 220 : 320}
                        primary={editing.primary_color}
                        accent={editing.accent_color}
                        icon={editing.icon || 'mic'}
                      />
                    )}
                  </div>
                ) : (
                  <ChatWidgetPreview
                    primary={editing.primary_color}
                    accent={editing.accent_color}
                    displayName={editing.display_name || editing.name}
                    welcome={editing.welcome_message}
                    prompts={(editing.suggested_prompts || []).filter(p => p && p.trim())}
                    launcherStyle={editing.launcher_style || 'bubble'}
                    launcherLabel={editing.launcher_label || 'Chat with us'}
                    launcherIcon={editing.launcher_icon || 'message'}
                    popupMessage={editing.popup_message || ''}
                    showPopup={editing.show_popup !== false}
                    popupStyle={editing.popup_style || 'text'}
                    popupAvatarUrl={editing.popup_avatar_url || ''}
                    popupBubbleTheme={editing.popup_bubble_theme || 'light'}
                    position={editing.position || 'bottom-right'}
                    isOpen={previewOpen}
                    onToggle={() => setPreviewOpen(o => !o)}
                    widgetTheme={editing.widget_theme || 'light'}
                    platformDark={isDarkMode}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* CHOOSE-TYPE MODAL — opened via "Create New Widget" button. Two big
        option cards that route to the correct builder. Kept intentionally
        high-contrast and hover-animated so the choice feels deliberate. */}
    <Dialog open={chooserOpen} onOpenChange={setChooserOpen}>
      <DialogContent
        className={`max-w-3xl ${isDarkMode ? 'bg-[#0f0f10] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold tracking-tight">
            Choose a widget type
          </DialogTitle>
          <DialogDescription className={t.textMuted}>
            Both work anywhere — pick the format that fits your host page and audience.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          {/* ── Orb Widget option ── */}
          <button
            type="button"
            onClick={() => { setChooserOpen(false); startCreate('orb'); }}
            data-testid="chooser-option-orb"
            className={`group relative text-left p-7 rounded-2xl border-2 transition-all overflow-hidden ${
              isDarkMode
                ? 'border-white/10 bg-white/[0.02] hover:border-teal-400/50 hover:bg-teal-400/[0.04]'
                : 'border-gray-200 bg-white hover:border-teal-400 hover:shadow-lg'
            }`}
          >
            {/* Soft teal glow on hover */}
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-teal-400/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div
                className="w-16 h-16 rounded-full mb-5 flex items-center justify-center shadow-lg"
                style={{ background: 'radial-gradient(circle at 35% 30%, #66ffe0 0%, #1a5eff 55%, #0a0a23 100%)' }}
              >
                <Mic size={24} strokeWidth={2} className="text-white/90" />
              </div>
              <h3 className={`text-lg font-semibold ${t.text} mb-1.5`}>Orb Widget</h3>
              <p className={`text-sm leading-relaxed ${t.textMuted}`}>
                A floating voice orb — click to talk. Best for voice-first conversion on landing pages, hero sections, and product tours.
              </p>
              <div className="mt-5 flex items-center gap-1.5 text-xs font-medium text-teal-500 opacity-80 group-hover:opacity-100 transition-opacity">
                <Sparkles size={12} /> Voice-first
              </div>
            </div>
          </button>

          {/* ── Chat Widget option ── */}
          <button
            type="button"
            onClick={() => { setChooserOpen(false); startCreate('widget'); }}
            data-testid="chooser-option-widget"
            className={`group relative text-left p-7 rounded-2xl border-2 transition-all overflow-hidden ${
              isDarkMode
                ? 'border-white/10 bg-white/[0.02] hover:border-violet-400/50 hover:bg-violet-400/[0.04]'
                : 'border-gray-200 bg-white hover:border-violet-400 hover:shadow-lg'
            }`}
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-violet-400/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div
                className="w-16 h-16 rounded-2xl mb-5 flex items-center justify-center shadow-lg"
                style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 60%, #4c1d95 100%)' }}
              >
                <MessageCircle size={24} strokeWidth={2} className="text-white/90" />
              </div>
              <h3 className={`text-lg font-semibold ${t.text} mb-1.5`}>Chat Widget</h3>
              <p className={`text-sm leading-relaxed ${t.textMuted}`}>
                A bubble chat widget in the corner of your site. Best for support, FAQ deflection, and asynchronous lead capture.
              </p>
              <div className="mt-5 flex items-center gap-1.5 text-xs font-medium text-violet-400 opacity-80 group-hover:opacity-100 transition-opacity">
                <MessageCircle size={12} /> Text-first
              </div>
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>

    {/* GET-CODE MODAL — opened via the "Get embed code" button under the orb
        settings. Shows the exact iframe snippet to paste into LeadConnector / any site,
        with a primary Copy button. Dialog auto-focuses the copy action so the
        common case is one click. */}
    <Dialog open={codeOpen} onOpenChange={setCodeOpen}>
      <DialogContent className={`max-w-2xl ${isDarkMode ? 'bg-[#0f0f10] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code size={18} className="text-emerald-500" />
            Embed code for {editing?.name}
          </DialogTitle>
          <DialogDescription className={t.textMuted}>
            Paste this anywhere you want the orb to appear — LeadConnector Custom HTML, your website, a landing page. The iframe carries all the styling and config from this widget.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <pre
            data-testid="embed-code-snippet"
            className={`block text-xs ${isDarkMode ? 'text-gray-200 bg-black/60 border-white/5' : 'text-gray-800 bg-gray-50 border-gray-200'} rounded-lg p-4 pr-16 break-all whitespace-pre-wrap font-mono leading-relaxed max-h-[300px] overflow-auto border`}
          >
{embedSnippet}
          </pre>
          <button
            onClick={copySnippet}
            data-testid="copy-embed-snippet"
            className="absolute top-3 right-3 px-3 py-1.5 rounded-lg bg-emerald-400/20 text-emerald-600 hover:bg-emerald-400/30 text-xs flex items-center gap-1.5 font-medium"
          >
            {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
          </button>
        </div>
        <p className={`text-[11px] ${t.textFaint} leading-relaxed`}>
          Size and position are controlled by the host page CSS — you can nest the iframe in any container. Every time you save changes here, embedded sites pick them up on the next visitor load (no re-copy needed).
        </p>
      </DialogContent>
    </Dialog>
    </SubAccountLayout>
  );
}

// ─── Subcomponents ───
// `t` is the theme token bag from the parent. Optional — falls back to dark-
// mode classes when omitted, so existing call sites keep working.
const Field = ({ label, hint, children, t }) => (
  <div>
    <label className={`block text-xs font-semibold ${t?.label || 'text-gray-300'} uppercase tracking-wider mb-2`}>{label}</label>
    {children}
    {hint && <p className={`text-[11px] ${t?.textFaint || 'text-gray-500'} mt-2`}>{hint}</p>}
  </div>
);

const ColorPicker = ({ value, onChange, testId, t }) => (
  <div className="flex items-center gap-2">
    <input type="color" value={value} onChange={e => onChange(e.target.value)}
      className={`w-11 h-11 rounded-lg cursor-pointer bg-transparent border ${t?.inputBorder || 'border-white/10'}`} data-testid={testId} />
    <input type="text" value={value} onChange={e => onChange(e.target.value)}
      className={`flex-1 px-3 py-2.5 rounded-lg ${t?.inputBg || 'bg-white/5'} border ${t?.inputBorder || 'border-white/10'} text-xs font-mono focus:outline-none focus:border-teal-400/50 ${t?.text || 'text-white'}`} />
  </div>
);

// Avatar uploader for the Avatar+Text pop-up style. Reads the file
// client-side, center-crops & scales to a 96×96 PNG via canvas, and
// emits a `data:image/png;base64,...` URL to the parent. We store
// the resized data-URL directly on the widget config — keeps avatar
// state alongside everything else with zero extra infra.
const AvatarUploader = ({ value, onChange, t }) => {
  const inputRef = useRef(null);
  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('That image is huge (over 5 MB). Try a smaller source file.');
      return;
    }
    try {
      const dataUrl = await resizeImageToDataUrl(file, 96, 0.85);
      onChange(dataUrl);
    } catch (e) {
      toast.error(e?.message || 'Could not process that image.');
    }
  };
  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };
  return (
    <div className="flex items-center gap-3">
      {value ? (
        <img
          src={value}
          alt="avatar preview"
          className="w-16 h-16 rounded-full object-cover border border-white/10"
          data-testid="avatar-preview"
        />
      ) : (
        <div className={`w-16 h-16 rounded-full border-2 border-dashed ${t?.inputBorder || 'border-white/15'} flex items-center justify-center ${t?.textFaint || 'text-gray-500'}`}>
          <ImageIcon size={20} />
        </div>
      )}
      <div className="flex-1">
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => inputRef.current?.click()}
          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer border ${t?.inputBg || 'bg-white/5'} ${t?.inputBorder || 'border-white/10'} text-sm ${t?.text || 'text-white'} hover:border-teal-400/50 transition`}
          data-testid="avatar-upload-dropzone"
        >
          <Upload size={16} />
          <span className="truncate">{value ? 'Replace image' : 'Click or drag an image here'}</span>
        </div>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className={`mt-2 text-[11px] inline-flex items-center gap-1 ${t?.textMuted || 'text-gray-400'} hover:text-rose-400 transition`}
            data-testid="avatar-remove"
          >
            <X size={11} /> Remove image
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="avatar-upload-input"
        />
      </div>
    </div>
  );
};

/**
 * Chat widget preview — purely visual. Two render modes controlled by
 * `isOpen` (driven from the parent so the pane-level "Open/Closed" toggle
 * can flip it). When open we show a bigger chat panel with a small typing
 * demo animation (3 dots → mock assistant reply), a cleaner rectangular
 * prompt-card row, and theme-aware colors via `widgetTheme` ('light' |
 * 'dark' | 'auto'). When closed we show the launcher (bubble or
 * rectangle) plus an optional popup teaser bubble next to it.
 *
 * Nothing here talks to the backend — the live conversation backend
 * lands in Phase 2. This component only persists the visual settings
 * so that when Phase 2 ships, every existing widget already has the
 * operator's preferred look.
 */
const ChatWidgetPreview = ({
  primary,
  accent,
  displayName,
  welcome,
  prompts = [],
  launcherStyle = 'bubble',
  launcherLabel = 'Chat with us',
  launcherIcon = 'message',
  popupMessage = '',
  showPopup = true,
  popupStyle = 'text',
  popupAvatarUrl = '',
  popupBubbleTheme = 'light',
  position = 'bottom-right',
  isOpen = true,                 // driven by parent pane toggle
  onToggle,                      // clicking the launcher / X calls this
  widgetTheme = 'light',
  platformDark = false,          // used when widgetTheme === 'auto'
}) => {
  // Map launcher icon key → lucide component. Same five options as the orb.
  const LAUNCHER_ICONS = { mic: Mic, phone: Phone, message: MessageCircle, headphones: Headphones, waveform: AudioLines };
  const LauncherIcon = LAUNCHER_ICONS[launcherIcon] || MessageCircle;
  // ─── Resolved theme for the widget itself ─────────────────────
  // Legacy configs may have 'auto' saved — treat as light for safety.
  const resolved = widgetTheme === 'dark' ? 'dark' : 'light';
  const dark = resolved === 'dark';
  const surface = dark ? '#0f1014' : '#ffffff';
  const surfaceAlt = dark ? '#0a0b0e' : '#f9fafb';
  const textMain = dark ? '#e5e7eb' : '#111827';
  const textDim = dark ? '#9ca3af' : '#6b7280';
  const bubbleBg = dark ? 'rgba(255,255,255,0.06)' : '#f3f4f6';
  const borderCol = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  // Suggested-prompt cards — must read as buttons in BOTH themes. We
  // use a subtle primary-tinted background, a stronger primary-tinted
  // border, and an arrow chevron to make the affordance obvious.
  const promptBg = dark ? `${primary}1A` : `${primary}10`;
  const promptBorder = dark ? `${primary}55` : `${primary}40`;
  const promptText = dark ? '#ffffff' : primary;
  const inputBg = dark ? '#0a0b0e' : '#ffffff';

  // ─── Typing-dots demo sequence ───
  // 1.5s typing → mock assistant reply for the rest of the preview. Reset
  // every time welcome or open state changes so operators get fresh
  // feedback as they edit copy.
  const [isTyping, setIsTyping] = useState(true);
  const [showReply, setShowReply] = useState(false);
  useEffect(() => {
    if (!isOpen) return undefined;
    setIsTyping(true); setShowReply(false);
    const t1 = setTimeout(() => setIsTyping(false), 1800);
    const t2 = setTimeout(() => setShowReply(true), 1900);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isOpen, welcome]);

  // ─── CLOSED STATE — launcher + optional popup teaser ─────────
  if (!isOpen) {
    // Resolve position-driven layout. Same `position` field the deployed
    // widget uses (`bottom-right` | `bottom-left`).
    const onLeft = String(position || 'bottom-right').includes('left');
    const sideClass = onLeft ? 'left-6' : 'right-6';
    const alignClass = onLeft ? 'items-start' : 'items-end';
    // Auto-contrast text color for the colored launcher / text-only
    // pop-up. Match the backend `_contrast_text_for` so the preview
    // and the deployed widget agree.
    const iconText = getContrastTextColor(primary);
    const useAvatarPopup = popupStyle === 'avatar_text' && (popupAvatarUrl || '').trim();
    const bubbleBgPop = popupBubbleTheme === 'dark' ? '#1a1d24' : '#ffffff';
    const bubbleTextPop = popupBubbleTheme === 'dark' ? '#ffffff' : '#0f172a';
    const bubbleDimPop = popupBubbleTheme === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)';
    return (
      <div className="relative h-full" data-testid="chat-preview-closed">
        <div className={`absolute bottom-6 ${sideClass} flex flex-col ${alignClass} gap-3`}>
          {launcherStyle === 'bubble' && showPopup && popupMessage.trim() && (
            useAvatarPopup ? (
              <div
                className="relative max-w-[300px] px-9 py-3.5 pl-3.5 rounded-2xl shadow-lg text-sm leading-snug flex items-center gap-3"
                style={{ background: bubbleBgPop, color: bubbleTextPop, border: '1px solid rgba(0,0,0,0.06)', whiteSpace: 'pre-wrap' }}
                data-testid="chat-preview-popup-avatar"
              >
                <img
                  src={popupAvatarUrl}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                />
                <div className="font-medium">{popupMessage}</div>
                <button
                  type="button"
                  aria-label="Dismiss"
                  className="absolute top-1.5 right-2 w-4 h-4 flex items-center justify-center"
                  style={{ color: bubbleDimPop }}
                >
                  <X size={12} />
                </button>
                <div
                  className={`absolute -bottom-1.5 ${onLeft ? 'left-6' : 'right-6'} w-3 h-3 rotate-45`}
                  style={{ background: bubbleBgPop, borderRight: '1px solid rgba(0,0,0,0.06)', borderBottom: '1px solid rgba(0,0,0,0.06)' }}
                />
              </div>
            ) : (
              <div
                className="relative max-w-[260px] px-4 py-3 rounded-2xl shadow-lg text-sm font-medium leading-snug"
                style={{ background: primary, color: iconText, whiteSpace: 'pre-wrap' }}
                data-testid="chat-preview-popup"
              >
                {popupMessage}
                <div
                  className={`absolute -bottom-1.5 ${onLeft ? 'left-6' : 'right-6'} w-3 h-3 rotate-45`}
                  style={{ background: primary }}
                />
              </div>
            )
          )}
          {launcherStyle === 'rectangle' ? (
            <button
              onClick={onToggle}
              className="flex items-center gap-2.5 px-5 py-3.5 rounded-full shadow-xl hover:shadow-2xl transition-shadow font-semibold text-sm"
              style={{ background: primary, color: iconText }}
              data-testid="chat-preview-launcher-rectangle"
            >
              <LauncherIcon size={18} strokeWidth={2.25} />
              {launcherLabel || 'Chat with us'}
            </button>
          ) : (
            <button
              onClick={onToggle}
              className="w-16 h-16 rounded-full shadow-xl hover:shadow-2xl transition-shadow flex items-center justify-center"
              style={{ background: primary }}
              data-testid="chat-preview-launcher-bubble"
            >
              <LauncherIcon size={26} strokeWidth={2} style={{ color: iconText }} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── OPEN STATE — bigger chat panel ──────────────────────────
  const displayInitial = (displayName || 'A').slice(0, 1).toUpperCase();
  // Header text contrasts the primary color (same logic the deployed
  // widget uses for parity).
  const headerText = getContrastTextColor(primary);
  const useImageAvatar = popupStyle === 'avatar_text' && (popupAvatarUrl || '').trim();
  // Pin the panel to the same side the launcher uses so the preview
  // mirrors the deployed widget.
  const onLeftOpen = String(position || 'bottom-right').includes('left');
  const panelSideClass = onLeftOpen ? 'left-8' : 'right-8';

  return (
    <div className="relative h-full" data-testid="chat-preview-open">
      <div
        className={`absolute bottom-8 ${panelSideClass} w-[440px] rounded-2xl shadow-2xl overflow-hidden flex flex-col`}
        style={{ background: surface, border: `1px solid ${borderCol}`, height: 640 }}
      >
        {/* Header */}
        <div
          className="px-4 py-3.5 flex items-center justify-between"
          style={{ background: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)` }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/25 flex items-center justify-center text-base font-semibold backdrop-blur-sm overflow-hidden" style={{ color: headerText }}>
              {useImageAvatar ? (
                <img src={popupAvatarUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                displayInitial
              )}
            </div>
            <div>
              <p className="text-sm font-semibold leading-tight" style={{ color: headerText }}>{displayName || 'Assistant'}</p>
              <p className="text-[11px] flex items-center gap-1" style={{ color: headerText, opacity: 0.85 }}>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 inline-block" />
                Online
              </p>
            </div>
          </div>
          <button
            onClick={onToggle}
            className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Close chat"
            data-testid="chat-preview-close-btn"
          >
            <span className="text-white text-lg leading-none">×</span>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 p-4 space-y-3 overflow-hidden" style={{ background: surface }}>
          {/* Welcome message from assistant */}
          <div
            className="max-w-[85%] rounded-2xl rounded-tl-sm px-3.5 py-2.5"
            style={{ background: bubbleBg }}
          >
            <p className="text-sm leading-relaxed" style={{ color: textMain }}>
              {welcome || 'Hi! How can I help you today?'}
            </p>
          </div>

          {/* Suggested prompts — distinct, button-like cards (primary-tinted) */}
          {prompts.length > 0 && (
            <div className="flex flex-col gap-2 pt-1">
              {prompts.map((p, i) => (
                <button
                  key={i}
                  className="text-left px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all hover:-translate-y-0.5 hover:shadow-md flex items-center justify-between gap-2 group"
                  style={{
                    background: promptBg,
                    border: `1px solid ${promptBorder}`,
                    color: promptText,
                  }}
                  data-testid={`chat-preview-prompt-${i}`}
                >
                  <span className="flex-1 truncate">{p}</span>
                  <span
                    className="opacity-60 group-hover:opacity-100 transition-opacity shrink-0"
                    style={{ color: primary }}
                    aria-hidden="true"
                  >
                    →
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* User "question" bubble (mock demo — triggers the typing state) */}
          {prompts[0] && (
            <div className="flex justify-end pt-2">
              <div
                className="max-w-[80%] rounded-2xl rounded-tr-sm px-3.5 py-2.5 text-sm"
                style={{ background: primary, color: headerText }}
              >
                {prompts[0]}
              </div>
            </div>
          )}

          {/* Typing dots OR mock assistant reply */}
          {isTyping && (
            <div
              className="w-fit max-w-[85%] rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1.5"
              style={{ background: bubbleBg }}
              data-testid="chat-preview-typing"
            >
              <span className="w-1.5 h-1.5 rounded-full animate-bounce [animation-delay:-0.3s]" style={{ background: textDim }} />
              <span className="w-1.5 h-1.5 rounded-full animate-bounce [animation-delay:-0.15s]" style={{ background: textDim }} />
              <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: textDim }} />
            </div>
          )}
          {showReply && (
            <div
              className="max-w-[85%] rounded-2xl rounded-tl-sm px-3.5 py-2.5"
              style={{ background: bubbleBg }}
            >
              <p className="text-sm leading-relaxed" style={{ color: textMain }}>
                Great question! Here's how I'd typically handle that — this is a preview, so replies shown here are just for demo.
              </p>
            </div>
          )}
        </div>

        {/* Input */}
        <div
          className="px-3 py-2.5 border-t flex items-center gap-2"
          style={{ borderColor: borderCol, background: surfaceAlt }}
        >
          <input
            disabled
            placeholder="Type a message…"
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: textDim }}
          />
          <button
            disabled
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: `${primary}26`, color: primary }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
};
